import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { CircularProgress, Box } from '@mui/material';
import { logger } from '../utils/logger';

interface FirstAdminGuardProps {
  children: React.ReactNode;
}

export const FirstAdminGuard: React.FC<FirstAdminGuardProps> = ({ children }) => {
  const [needsAdmin, setNeedsAdmin] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Solo verificar una vez al montar o cuando cambiamos a rutas relevantes
    // Evitar verificar en cada cambio de ruta para prevenir bucles
    const shouldCheck = !hasChecked || 
                       (needsAdmin === true && location.pathname !== '/create-first-admin') ||
                       (needsAdmin === false && location.pathname === '/create-first-admin');
    
    if (!shouldCheck) {
      if (isChecking) {
        setIsChecking(false);
      }
      return;
    }
    
    const checkNeedsAdmin = async () => {
      setIsChecking(true);
      try {
        const response = await apiService.needsFirstAdmin();
        setNeedsAdmin(response.needs);
        setHasChecked(true);
      } catch (error) {
        logger.error('Error checking if admin is needed:', error);
        // En caso de error, asumimos que no necesita admin para no bloquear la app
        setNeedsAdmin(false);
        setHasChecked(true);
      } finally {
        setIsChecking(false);
      }
    };

    checkNeedsAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Si está verificando, mostrar loading
  if (isChecking) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Si necesita admin y no está en la ruta de creación, redirigir
  if (needsAdmin && location.pathname !== '/create-first-admin') {
    return <Navigate to="/create-first-admin" replace />;
  }

  // Si no necesita admin y está en la ruta de creación, redirigir a login
  if (!needsAdmin && location.pathname === '/create-first-admin') {
    return <Navigate to="/login" replace />;
  }

  // En cualquier otro caso, mostrar el contenido
  return <>{children}</>;
};

